import React from 'react'
import { useTranslation } from 'react-i18next'
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline'

/**
 * React Error Boundary component that catches JavaScript errors in child components.
 * Displays a user-friendly fallback UI instead of crashing the entire app.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomeComponent />
 *   </ErrorBoundary>
 *
 *   <ErrorBoundary fallback={<CustomFallback />}>
 *     <SomeComponent />
 *   </ErrorBoundary>
 */
class ErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <ErrorFallback
          error={this.state.error}
          onReset={this.handleReset}
          title={this.props.title}
        />
      )
    }

    return this.props.children
  }
}

function ErrorFallback({ error, onReset, title }) {
  const { t } = useTranslation()

  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
      <ExclamationTriangleIcon className="h-10 w-10 text-red-400 mx-auto mb-3" />
      <h3 className="text-lg font-medium text-red-800 mb-1">
        {title || t('errorBoundary.title')}
      </h3>
      <p className="text-sm text-red-600 mb-4">
        {t('errorBoundary.message')}
      </p>
      {/* The technical message is shown in every environment: without it a production
          failure can only be reported as "something went wrong", which is not diagnosable. */}
      {error && (
        <details className="text-left mb-4">
          <summary className="text-xs text-red-700 cursor-pointer">{t('errorBoundary.details')}</summary>
          <pre className="text-xs bg-red-100 rounded p-3 mt-2 overflow-auto max-h-40 whitespace-pre-wrap" data-testid="error-boundary-details">
            {[error.message, error.stack?.split('\n').slice(1, 4).join('\n')].filter(Boolean).join('\n')}
          </pre>
        </details>
      )}
      <button
        onClick={onReset}
        className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-700 bg-surface-container border border-red-300 rounded-md hover:bg-red-50"
      >
        <ArrowPathIcon className="h-4 w-4 mr-2" />
        {t('errorBoundary.retry')}
      </button>
    </div>
  )
}

export default function ErrorBoundary({ children, fallback, title }) {
  return (
    <ErrorBoundaryInner fallback={fallback} title={title}>
      {children}
    </ErrorBoundaryInner>
  )
}
